import { NavLink, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LayoutDashboard, LogOut, ScrollText, Store } from 'lucide-react';
import { api, post } from '../lib/api';
import { Button, GarlicLogo, Spinner, cx } from '../ui/primitives';
import { DevLogin } from './DevLogin';
import { DevDashboard } from './DevDashboard';
import { RestaurantList } from './RestaurantList';
import { RestaurantCreate } from './RestaurantCreate';
import { RestaurantDetail } from './RestaurantDetail';
import { DevAudit } from './DevAudit';

export type DevSession = {
  authenticated: boolean;
  next?: 'mfa_setup' | 'mfa_verify' | null;
  admin?: { name: string; email: string };
};

export function useDevSession() {
  return useQuery({ queryKey: ['dev-session'], queryFn: () => api<DevSession>('/api/dev/auth/session'), staleTime: 30_000 });
}

export function DevApp() {
  const session = useDevSession();
  if (session.isPending) return <Spinner />;
  if (!session.data?.authenticated) return <DevLogin session={session.data} />;
  return <DevShell admin={session.data.admin!} />;
}

const nav = [
  { to: '/dev', label: 'Visão geral', icon: LayoutDashboard, end: true },
  { to: '/dev/restaurantes', label: 'Restaurantes', icon: Store },
  { to: '/dev/auditoria', label: 'Auditoria', icon: ScrollText },
];

function DevShell({ admin }: { admin: { name: string; email: string } }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const logout = async () => {
    await post('/api/dev/auth/logout').catch(() => {});
    qc.clear();
    navigate('/dev');
    await qc.invalidateQueries({ queryKey: ['dev-session'] });
  };
  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[15rem_1fr]">
      <aside className="border-b border-line bg-white lg:sticky lg:top-0 lg:flex lg:h-dvh lg:flex-col lg:border-b-0 lg:border-r">
        <div className="flex items-center justify-between px-4 py-4 lg:px-5 lg:py-6">
          <GarlicLogo suffix="dev" />
          <Button variant="ghost" size="sm" className="lg:hidden" onClick={logout} icon={<LogOut className="size-4" />}>
            Sair
          </Button>
        </div>
        <nav className="flex gap-1 overflow-x-auto px-3 pb-3 lg:flex-1 lg:flex-col lg:overflow-visible lg:pb-0">
          {nav.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                cx(
                  'flex shrink-0 items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-medium transition-colors',
                  isActive ? 'bg-garlic-50 text-garlic-700' : 'text-ink-2 hover:bg-paper-2 hover:text-ink',
                )
              }
            >
              <Icon className="size-4" aria-hidden />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden border-t border-line p-4 lg:block">
          <p className="truncate text-sm font-medium">{admin.name}</p>
          <p className="truncate text-xs text-ink-3">{admin.email}</p>
          <Button variant="secondary" size="sm" className="mt-3 w-full" onClick={logout} icon={<LogOut className="size-4" />}>
            Sair
          </Button>
        </div>
      </aside>
      <main className="min-w-0 px-4 py-6 sm:px-8 sm:py-8">
        <div className="mx-auto max-w-6xl">
          <Routes>
            <Route index element={<DevDashboard />} />
            <Route path="restaurantes" element={<RestaurantList />} />
            <Route path="restaurantes/novo" element={<RestaurantCreate />} />
            <Route path="restaurantes/:id" element={<RestaurantDetail />} />
            <Route path="auditoria" element={<DevAudit />} />
            <Route path="*" element={<Navigate to="/dev" replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
